/**
 * @module useFlightAttendant
 */
import { useCallback } from 'react'
import {
  useGetFlightAttendantsMutation,
  useGetFlightAttendantByIdMutation,
  useGetFlightAttendantByEmployeeNumberMutation,
  useCreateFlightAttendantMutation,
  useUpdateFlightAttendantMutation,
  useDeleteFlightAttendantMutation,
  useUpdateStatusMutation,
  useUpdateCertificationsMutation,
  useUpdateContactInfoMutation,
  useUpdateMedicalInfoMutation,
  useUpdateUniformMutation,
  useAddLanguageMutation,
  useRemoveLanguageMutation,
  useAddAircraftTypeMutation,
  useRemoveAircraftTypeMutation,
  useAddSpecialQualificationMutation,
  useRemoveSpecialQualificationMutation,
  useUpdateAvailabilityMutation,
  useRequestTimeOffMutation,
  useUpdateTimeOffRequestMutation,
  useAddEvaluationMutation,
  useAddIncidentMutation,
  useAddCommendationMutation,
  useTerminateMutation,
  useReactivateMutation,
  useGetActiveFlightAttendantsMutation,
  useGetFlightAttendantsByAirlineMutation,
  useGetFlightAttendantsByAircraftMutation,
  useGetFlightAttendantsByBaseAirportMutation,
  useGetFlightAttendantsNeedingRecurrentTrainingMutation,
  useGetFlightAttendantsWithExpiringMedicalMutation,
  useGetFlightAttendantsWithExpiringEmergencyTrainingMutation,
} from '../services/flightAttendant.service'

export const useFlightAttendant = () => {
  // Basic CRUD operations
  const [executeGetFlightAttendants, { isLoading: isGetFlightAttendantsLoading }] =
    useGetFlightAttendantsMutation()
  const [executeGetFlightAttendantById, { isLoading: isGetFlightAttendantByIdLoading }] =
    useGetFlightAttendantByIdMutation()
  const [
    executeGetFlightAttendantByEmployeeNumber,
    { isLoading: isGetFlightAttendantByEmployeeNumberLoading },
  ] = useGetFlightAttendantByEmployeeNumberMutation()
  const [executeCreateFlightAttendant, { isLoading: isCreateFlightAttendantLoading }] =
    useCreateFlightAttendantMutation()
  const [executeUpdateFlightAttendant, { isLoading: isUpdateFlightAttendantLoading }] =
    useUpdateFlightAttendantMutation()
  const [executeDeleteFlightAttendant, { isLoading: isDeleteFlightAttendantLoading }] =
    useDeleteFlightAttendantMutation()

  // Status and profile updates
  const [executeUpdateStatus, { isLoading: isUpdateStatusLoading }] = useUpdateStatusMutation()
  const [executeUpdateCertifications, { isLoading: isUpdateCertificationsLoading }] =
    useUpdateCertificationsMutation()
  const [executeUpdateContactInfo, { isLoading: isUpdateContactInfoLoading }] =
    useUpdateContactInfoMutation()
  const [executeUpdateMedicalInfo, { isLoading: isUpdateMedicalInfoLoading }] =
    useUpdateMedicalInfoMutation()
  const [executeUpdateUniform, { isLoading: isUpdateUniformLoading }] = useUpdateUniformMutation()

  // Language operations
  const [executeAddLanguage, { isLoading: isAddLanguageLoading }] = useAddLanguageMutation()
  const [executeRemoveLanguage, { isLoading: isRemoveLanguageLoading }] =
    useRemoveLanguageMutation()

  // Aircraft type operations
  const [executeAddAircraftType, { isLoading: isAddAircraftTypeLoading }] =
    useAddAircraftTypeMutation()
  const [executeRemoveAircraftType, { isLoading: isRemoveAircraftTypeLoading }] =
    useRemoveAircraftTypeMutation()

  // Special qualification operations
  const [executeAddSpecialQualification, { isLoading: isAddSpecialQualificationLoading }] =
    useAddSpecialQualificationMutation()
  const [executeRemoveSpecialQualification, { isLoading: isRemoveSpecialQualificationLoading }] =
    useRemoveSpecialQualificationMutation()

  // Schedule operations
  const [executeUpdateAvailability, { isLoading: isUpdateAvailabilityLoading }] =
    useUpdateAvailabilityMutation()
  const [executeRequestTimeOff, { isLoading: isRequestTimeOffLoading }] =
    useRequestTimeOffMutation()
  const [executeUpdateTimeOffRequest, { isLoading: isUpdateTimeOffRequestLoading }] =
    useUpdateTimeOffRequestMutation()

  // Performance operations
  const [executeAddEvaluation, { isLoading: isAddEvaluationLoading }] = useAddEvaluationMutation()
  const [executeAddIncident, { isLoading: isAddIncidentLoading }] = useAddIncidentMutation()
  const [executeAddCommendation, { isLoading: isAddCommendationLoading }] =
    useAddCommendationMutation()

  // Employment status
  const [executeTerminate, { isLoading: isTerminateLoading }] = useTerminateMutation()
  const [executeReactivate, { isLoading: isReactivateLoading }] = useReactivateMutation()

  // Query operations
  const [executeGetActiveFlightAttendants, { isLoading: isGetActiveFlightAttendantsLoading }] =
    useGetActiveFlightAttendantsMutation()
  const [
    executeGetFlightAttendantsByAirline,
    { isLoading: isGetFlightAttendantsByAirlineLoading },
  ] = useGetFlightAttendantsByAirlineMutation()
  const [
    executeGetFlightAttendantsByAircraft,
    { isLoading: isGetFlightAttendantsByAircraftLoading },
  ] = useGetFlightAttendantsByAircraftMutation()
  const [
    executeGetFlightAttendantsByBaseAirport,
    { isLoading: isGetFlightAttendantsByBaseAirportLoading },
  ] = useGetFlightAttendantsByBaseAirportMutation()
  // Compliance and monitoring
  const [
    executeGetFlightAttendantsNeedingRecurrentTraining,
    { isLoading: isGetFlightAttendantsNeedingRecurrentTrainingLoading },
  ] = useGetFlightAttendantsNeedingRecurrentTrainingMutation()
  const [
    executeGetFlightAttendantsWithExpiringMedical,
    { isLoading: isGetFlightAttendantsWithExpiringMedicalLoading },
  ] = useGetFlightAttendantsWithExpiringMedicalMutation()
  const [
    executeGetFlightAttendantsWithExpiringEmergencyTraining,
    { isLoading: isGetFlightAttendantsWithExpiringEmergencyTrainingLoading },
  ] = useGetFlightAttendantsWithExpiringEmergencyTrainingMutation()

  // Basic CRUD async functions
  const getFlightAttendantsAsync = useCallback(async () => {
    if (isGetFlightAttendantsLoading) {
      return
    }
    try {
      const response = await executeGetFlightAttendants().unwrap()
      return response.data.flightAttendants
    } catch (error) {
      console.error('#getFlightAttendantsAsync: Error fetching flight attendants', error)
      throw error
    }
  }, [executeGetFlightAttendants, isGetFlightAttendantsLoading])

  const getFlightAttendantByIdAsync = useCallback(
    async (flightAttendantId) => {
      if (isGetFlightAttendantByIdLoading) {
        return
      }
      try {
        const response = await executeGetFlightAttendantById(flightAttendantId).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#getFlightAttendantByIdAsync: Error fetching flight attendant by ID', error)
        throw error
      }
    },
    [executeGetFlightAttendantById, isGetFlightAttendantByIdLoading],
  )

  const getFlightAttendantByEmployeeNumberAsync = useCallback(
    async (employeeNumber) => {
      if (isGetFlightAttendantByEmployeeNumberLoading) {
        return
      }
      try {
        const response = await executeGetFlightAttendantByEmployeeNumber(employeeNumber).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error(
          '#getFlightAttendantByEmployeeNumberAsync: Error fetching flight attendant by employee number',
          error,
        )
        throw error
      }
    },
    [executeGetFlightAttendantByEmployeeNumber, isGetFlightAttendantByEmployeeNumberLoading],
  )

  const createFlightAttendantAsync = useCallback(
    async (flightAttendant) => {
      if (isCreateFlightAttendantLoading) {
        return
      }
      try {
        const response = await executeCreateFlightAttendant(flightAttendant).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#createFlightAttendantAsync: Error creating flight attendant', error)
        throw error
      }
    },
    [executeCreateFlightAttendant, isCreateFlightAttendantLoading],
  )

  const updateFlightAttendantAsync = useCallback(
    async (flightAttendantId, updates) => {
      if (isUpdateFlightAttendantLoading) {
        return
      }
      try {
        const response = await executeUpdateFlightAttendant({
          flightAttendantId,
          ...updates,
        }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#updateFlightAttendantAsync: Error updating flight attendant', error)
        throw error
      }
    },
    [executeUpdateFlightAttendant, isUpdateFlightAttendantLoading],
  )

  const deleteFlightAttendantAsync = useCallback(
    async (flightAttendantId) => {
      if (isDeleteFlightAttendantLoading) {
        return
      }
      try {
        const response = await executeDeleteFlightAttendant(flightAttendantId).unwrap()
        return response.data
      } catch (error) {
        console.error('#deleteFlightAttendantAsync: Error deleting flight attendant', error)
        throw error
      }
    },
    [executeDeleteFlightAttendant, isDeleteFlightAttendantLoading],
  )

  // Status and profile async functions
  const updateStatusAsync = useCallback(
    async (flightAttendantId, status) => {
      if (isUpdateStatusLoading) {
        return
      }
      try {
        const response = await executeUpdateStatus({ flightAttendantId, status }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#updateStatusAsync: Error updating flight attendant status', error)
        throw error
      }
    },
    [executeUpdateStatus, isUpdateStatusLoading],
  )

  const updateCertificationsAsync = useCallback(
    async (flightAttendantId, certifications) => {
      if (isUpdateCertificationsLoading) {
        return
      }
      try {
        const response = await executeUpdateCertifications({
          flightAttendantId,
          certifications,
        }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#updateCertificationsAsync: Error updating certifications', error)
        throw error
      }
    },
    [executeUpdateCertifications, isUpdateCertificationsLoading],
  )

  const updateContactInfoAsync = useCallback(
    async (flightAttendantId, contactInfo) => {
      if (isUpdateContactInfoLoading) {
        return
      }
      try {
        const response = await executeUpdateContactInfo({ flightAttendantId, contactInfo }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#updateContactInfoAsync: Error updating contact info', error)
        throw error
      }
    },
    [executeUpdateContactInfo, isUpdateContactInfoLoading],
  )

  const updateMedicalInfoAsync = useCallback(
    async (flightAttendantId, medicalInfo) => {
      if (isUpdateMedicalInfoLoading) {
        return
      }
      try {
        const response = await executeUpdateMedicalInfo({ flightAttendantId, medicalInfo }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#updateMedicalInfoAsync: Error updating medical info', error)
        throw error
      }
    },
    [executeUpdateMedicalInfo, isUpdateMedicalInfoLoading],
  )

  const updateUniformAsync = useCallback(
    async (flightAttendantId, uniform) => {
      if (isUpdateUniformLoading) {
        return
      }
      try {
        const response = await executeUpdateUniform({ flightAttendantId, uniform }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#updateUniformAsync: Error updating uniform', error)
        throw error
      }
    },
    [executeUpdateUniform, isUpdateUniformLoading],
  )

  // Language async functions
  const addLanguageAsync = useCallback(
    async (flightAttendantId, language) => {
      if (isAddLanguageLoading) {
        return
      }
      try {
        const response = await executeAddLanguage({ flightAttendantId, language }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#addLanguageAsync: Error adding language', error)
        throw error
      }
    },
    [executeAddLanguage, isAddLanguageLoading],
  )

  const removeLanguageAsync = useCallback(
    async (flightAttendantId, language) => {
      if (isRemoveLanguageLoading) {
        return
      }
      try {
        const response = await executeRemoveLanguage({ flightAttendantId, language }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#removeLanguageAsync: Error removing language', error)
        throw error
      }
    },
    [executeRemoveLanguage, isRemoveLanguageLoading],
  )

  // Aircraft type async functions
  const addAircraftTypeAsync = useCallback(
    async (flightAttendantId, aircraftType) => {
      if (isAddAircraftTypeLoading) {
        return
      }
      try {
        const response = await executeAddAircraftType({ flightAttendantId, aircraftType }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#addAircraftTypeAsync: Error adding aircraft type', error)
        throw error
      }
    },
    [executeAddAircraftType, isAddAircraftTypeLoading],
  )

  const removeAircraftTypeAsync = useCallback(
    async (flightAttendantId, aircraftType) => {
      if (isRemoveAircraftTypeLoading) {
        return
      }
      try {
        const response = await executeRemoveAircraftType({
          flightAttendantId,
          aircraftType,
        }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#removeAircraftTypeAsync: Error removing aircraft type', error)
        throw error
      }
    },
    [executeRemoveAircraftType, isRemoveAircraftTypeLoading],
  )

  // Special qualification async functions
  const addSpecialQualificationAsync = useCallback(
    async (flightAttendantId, qualification) => {
      if (isAddSpecialQualificationLoading) {
        return
      }
      try {
        const response = await executeAddSpecialQualification({
          flightAttendantId,
          qualification,
        }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#addSpecialQualificationAsync: Error adding special qualification', error)
        throw error
      }
    },
    [executeAddSpecialQualification, isAddSpecialQualificationLoading],
  )

  const removeSpecialQualificationAsync = useCallback(
    async (flightAttendantId, qualification) => {
      if (isRemoveSpecialQualificationLoading) {
        return
      }
      try {
        const response = await executeRemoveSpecialQualification({
          flightAttendantId,
          qualification,
        }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error(
          '#removeSpecialQualificationAsync: Error removing special qualification',
          error,
        )
        throw error
      }
    },
    [executeRemoveSpecialQualification, isRemoveSpecialQualificationLoading],
  )

  // Schedule async functions
  const updateAvailabilityAsync = useCallback(
    async (flightAttendantId, availability) => {
      if (isUpdateAvailabilityLoading) {
        return
      }
      try {
        const response = await executeUpdateAvailability({
          flightAttendantId,
          availability,
        }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#updateAvailabilityAsync: Error updating availability', error)
        throw error
      }
    },
    [executeUpdateAvailability, isUpdateAvailabilityLoading],
  )

  const requestTimeOffAsync = useCallback(
    async (flightAttendantId, timeOffRequest) => {
      if (isRequestTimeOffLoading) {
        return
      }
      try {
        const response = await executeRequestTimeOff({ flightAttendantId, timeOffRequest }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#requestTimeOffAsync: Error requesting time off', error)
        throw error
      }
    },
    [executeRequestTimeOff, isRequestTimeOffLoading],
  )

  const updateTimeOffRequestAsync = useCallback(
    async (flightAttendantId, timeOffRequest) => {
      if (isUpdateTimeOffRequestLoading) {
        return
      }
      try {
        const response = await executeUpdateTimeOffRequest({
          flightAttendantId,
          timeOffRequest,
        }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#updateTimeOffRequestAsync: Error updating time off request', error)
        throw error
      }
    },
    [executeUpdateTimeOffRequest, isUpdateTimeOffRequestLoading],
  )

  // Performance async functions
  const addEvaluationAsync = useCallback(
    async (flightAttendantId, evaluation) => {
      if (isAddEvaluationLoading) {
        return
      }
      try {
        const response = await executeAddEvaluation({ flightAttendantId, evaluation }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#addEvaluationAsync: Error adding evaluation', error)
        throw error
      }
    },
    [executeAddEvaluation, isAddEvaluationLoading],
  )

  const addIncidentAsync = useCallback(
    async (flightAttendantId, incident) => {
      if (isAddIncidentLoading) {
        return
      }
      try {
        const response = await executeAddIncident({ flightAttendantId, incident }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#addIncidentAsync: Error adding incident', error)
        throw error
      }
    },
    [executeAddIncident, isAddIncidentLoading],
  )

  const addCommendationAsync = useCallback(
    async (flightAttendantId, commendation) => {
      if (isAddCommendationLoading) {
        return
      }
      try {
        const response = await executeAddCommendation({ flightAttendantId, commendation }).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#addCommendationAsync: Error adding commendation', error)
        throw error
      }
    },
    [executeAddCommendation, isAddCommendationLoading],
  )

  // Employment status async functions
  const terminateAsync = useCallback(
    async (flightAttendantId) => {
      if (isTerminateLoading) {
        return
      }
      try {
        const response = await executeTerminate(flightAttendantId).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#terminateAsync: Error terminating flight attendant', error)
        throw error
      }
    },
    [executeTerminate, isTerminateLoading],
  )

  const reactivateAsync = useCallback(
    async (flightAttendantId) => {
      if (isReactivateLoading) {
        return
      }
      try {
        const response = await executeReactivate(flightAttendantId).unwrap()
        return response.data.flightAttendant
      } catch (error) {
        console.error('#reactivateAsync: Error reactivating flight attendant', error)
        throw error
      }
    },
    [executeReactivate, isReactivateLoading],
  )

  // Query async functions
  const getActiveFlightAttendantsAsync = useCallback(async () => {
    if (isGetActiveFlightAttendantsLoading) {
      return
    }
    try {
      const response = await executeGetActiveFlightAttendants().unwrap()
      return response.data.flightAttendants
    } catch (error) {
      console.error(
        '#getActiveFlightAttendantsAsync: Error fetching active flight attendants',
        error,
      )
      throw error
    }
  }, [executeGetActiveFlightAttendants, isGetActiveFlightAttendantsLoading])

  const getFlightAttendantsByAirlineAsync = useCallback(
    async (airlineId) => {
      if (isGetFlightAttendantsByAirlineLoading) {
        return
      }
      try {
        const response = await executeGetFlightAttendantsByAirline(airlineId).unwrap()
        return response.data.flightAttendants
      } catch (error) {
        console.error(
          '#getFlightAttendantsByAirlineAsync: Error fetching flight attendants by airline',
          error,
        )
        throw error
      }
    },
    [executeGetFlightAttendantsByAirline, isGetFlightAttendantsByAirlineLoading],
  )

  const getFlightAttendantsByAircraftAsync = useCallback(
    async (aircraftId) => {
      if (isGetFlightAttendantsByAircraftLoading) {
        return
      }
      try {
        const response = await executeGetFlightAttendantsByAircraft(aircraftId).unwrap()
        return response.data.flightAttendants
      } catch (error) {
        console.error(
          '#getFlightAttendantsByAircraftAsync: Error fetching flight attendants by aircraft',
          error,
        )
        throw error
      }
    },
    [executeGetFlightAttendantsByAircraft, isGetFlightAttendantsByAircraftLoading],
  )

  const getFlightAttendantsByBaseAirportAsync = useCallback(
    async (baseAirport) => {
      if (isGetFlightAttendantsByBaseAirportLoading) {
        return
      }
      try {
        const response = await executeGetFlightAttendantsByBaseAirport(baseAirport).unwrap()
        return response.data.flightAttendants
      } catch (error) {
        console.error(
          '#getFlightAttendantsByBaseAirportAsync: Error fetching flight attendants by base airport',
          error,
        )
        throw error
      }
    },
    [executeGetFlightAttendantsByBaseAirport, isGetFlightAttendantsByBaseAirportLoading],
  )

  // Compliance and monitoring async functions
  const getFlightAttendantsNeedingRecurrentTrainingAsync = useCallback(async () => {
    if (isGetFlightAttendantsNeedingRecurrentTrainingLoading) {
      return
    }
    try {
      const response = await executeGetFlightAttendantsNeedingRecurrentTraining().unwrap()
      return response.data.flightAttendants
    } catch (error) {
      console.error(
        '#getFlightAttendantsNeedingRecurrentTrainingAsync: Error fetching flight attendants needing recurrent training',
        error,
      )
      throw error
    }
  }, [
    executeGetFlightAttendantsNeedingRecurrentTraining,
    isGetFlightAttendantsNeedingRecurrentTrainingLoading,
  ])

  const getFlightAttendantsWithExpiringMedicalAsync = useCallback(async () => {
    if (isGetFlightAttendantsWithExpiringMedicalLoading) {
      return
    }
    try {
      const response = await executeGetFlightAttendantsWithExpiringMedical().unwrap()
      return response.data.flightAttendants
    } catch (error) {
      console.error(
        '#getFlightAttendantsWithExpiringMedicalAsync: Error fetching flight attendants with expiring medical',
        error,
      )
      throw error
    }
  }, [
    executeGetFlightAttendantsWithExpiringMedical,
    isGetFlightAttendantsWithExpiringMedicalLoading,
  ])

  const getFlightAttendantsWithExpiringEmergencyTrainingAsync = useCallback(async () => {
    if (isGetFlightAttendantsWithExpiringEmergencyTrainingLoading) {
      return
    }
    try {
      const response = await executeGetFlightAttendantsWithExpiringEmergencyTraining().unwrap()
      return response.data.flightAttendants
    } catch (error) {
      console.error(
        '#getFlightAttendantsWithExpiringEmergencyTrainingAsync: Error fetching flight attendants with expiring emergency training',
        error,
      )
      throw error
    }
  }, [
    executeGetFlightAttendantsWithExpiringEmergencyTraining,
    isGetFlightAttendantsWithExpiringEmergencyTrainingLoading,
  ])

  return {
    // Basic CRUD functions
    getFlightAttendantsAsync,
    getFlightAttendantByIdAsync,
    getFlightAttendantByEmployeeNumberAsync,
    createFlightAttendantAsync,
    updateFlightAttendantAsync,
    deleteFlightAttendantAsync,

    // Status and profile functions
    updateStatusAsync,
    updateCertificationsAsync,
    updateContactInfoAsync,
    updateMedicalInfoAsync,
    updateUniformAsync,

    // Language functions
    addLanguageAsync,
    removeLanguageAsync,

    // Aircraft type functions
    addAircraftTypeAsync,
    removeAircraftTypeAsync,

    // Special qualification functions
    addSpecialQualificationAsync,
    removeSpecialQualificationAsync,

    // Schedule functions
    updateAvailabilityAsync,
    requestTimeOffAsync,
    updateTimeOffRequestAsync,

    // Performance functions
    addEvaluationAsync,
    addIncidentAsync,
    addCommendationAsync,

    // Employment status functions
    terminateAsync,
    reactivateAsync,

    // Query functions
    getActiveFlightAttendantsAsync,
    getFlightAttendantsByAirlineAsync,
    getFlightAttendantsByAircraftAsync,
    getFlightAttendantsByBaseAirportAsync,

    // Compliance and monitoring functions
    getFlightAttendantsNeedingRecurrentTrainingAsync,
    getFlightAttendantsWithExpiringMedicalAsync,
    getFlightAttendantsWithExpiringEmergencyTrainingAsync,

    // Loading states
    isGetFlightAttendantsLoading,
    isGetFlightAttendantByIdLoading,
    isGetFlightAttendantByEmployeeNumberLoading,
    isCreateFlightAttendantLoading,
    isUpdateFlightAttendantLoading,
    isDeleteFlightAttendantLoading,
    isUpdateStatusLoading,
    isUpdateCertificationsLoading,
    isUpdateContactInfoLoading,
    isUpdateMedicalInfoLoading,
    isUpdateUniformLoading,
    isAddLanguageLoading,
    isRemoveLanguageLoading,
    isAddAircraftTypeLoading,
    isRemoveAircraftTypeLoading,
    isAddSpecialQualificationLoading,
    isRemoveSpecialQualificationLoading,
    isUpdateAvailabilityLoading,
    isRequestTimeOffLoading,
    isUpdateTimeOffRequestLoading,
    isAddEvaluationLoading,
    isAddIncidentLoading,
    isAddCommendationLoading,
    isTerminateLoading,
    isReactivateLoading,
    isGetActiveFlightAttendantsLoading,
    isGetFlightAttendantsByAirlineLoading,
    isGetFlightAttendantsByAircraftLoading,
    isGetFlightAttendantsByBaseAirportLoading,
    isGetFlightAttendantsNeedingRecurrentTrainingLoading,
    isGetFlightAttendantsWithExpiringMedicalLoading,
    isGetFlightAttendantsWithExpiringEmergencyTrainingLoading,
  }
}
